import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users,
  UserPlus,
  Activity,
  CreditCard,
  DollarSign,
  Repeat,
  AlertTriangle,
  TrendingUp,
  Sparkles,
  Calendar as CalendarIcon,
  Send,
  XCircle,
  Eye,
  Loader2,
  ShieldAlert,
  Link2,
  AlertCircle,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";
import GlassCard from "@/components/GlassCard";
import GradientMesh from "@/components/GradientMesh";
import PageHeader from "@/components/PageHeader";
import RebrandChecklist from "@/components/admin/RebrandChecklist";

function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: v % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });
}

function fmtInt(n) {
  return Number(n || 0).toLocaleString();
}

function MetricCard({ icon: Icon, label, value, sub, tone = "neutral", testid }) {
  const tones = {
    neutral: "text-[color:var(--text-1)]",
    brand: "text-[color:var(--brand-primary)]",
    warm: "text-[color:var(--accent-warm)]",
    success: "text-[color:var(--success)]",
    danger: "text-[color:var(--danger)]",
    info: "text-[color:var(--info)]",
  };
  return (
    <GlassCard className="p-4" data-testid={testid}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-[color:var(--text-3)]">{label}</div>
          <div className={"font-display text-2xl font-semibold mt-1 " + tones[tone]}>{value}</div>
          {sub ? <div className="text-xs text-[color:var(--text-3)] mt-1">{sub}</div> : null}
        </div>
        {Icon ? (
          <div className="h-9 w-9 rounded-md bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] flex items-center justify-center text-[color:var(--text-2)]">
            <Icon className="h-4 w-4" />
          </div>
        ) : null}
      </div>
    </GlassCard>
  );
}

function DataSourceLabel({ source }) {
  if (!source) return null;
  const map = {
    real: {
      label: "Real data",
      cls: "bg-[color:rgba(47,231,161,0.12)] text-[color:var(--success)] border-[color:rgba(47,231,161,0.35)]",
    },
    stripe_test: {
      label: "Stripe test data",
      cls: "bg-[color:rgba(77,183,255,0.12)] text-[color:var(--info)] border-[color:rgba(77,183,255,0.35)]",
    },
    stripe_live: {
      label: "Stripe live data",
      cls: "bg-[color:rgba(47,231,161,0.12)] text-[color:var(--success)] border-[color:rgba(47,231,161,0.35)]",
    },
    not_connected: {
      label: "Not connected yet",
      cls: "bg-[color:var(--surface-2)] text-[color:var(--text-3)] border-[color:var(--border-subtle)]",
    },
    demo: {
      label: "Demo data",
      cls: "bg-[color:rgba(255,138,61,0.14)] text-[color:var(--accent-warm)] border-[color:rgba(255,138,61,0.35)]",
    },
  };
  const entry = map[source] || map.real;
  return (
    <span
      className={`text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${entry.cls} inline-flex items-center gap-1`}
      data-testid={`data-source-${source}`}
    >
      <ShieldAlert className="h-3 w-3" /> {entry.label}
    </span>
  );
}

function NotConnectedPanel({ message, helper, testid }) {
  return (
    <div
      className="rounded-md border border-dashed border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-6 text-center"
      data-testid={testid}
    >
      <div className="font-display text-sm font-semibold text-[color:var(--text-2)]">{message}</div>
      {helper ? (
        <div className="text-[11px] text-[color:var(--text-3)] mt-1.5 max-w-md mx-auto leading-relaxed">
          {helper}
        </div>
      ) : null}
    </div>
  );
}

function ChartTooltip({ active, payload, label, formatter }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md bg-[color:var(--bg-0)] border border-[color:var(--border-subtle)] px-2.5 py-1.5 text-[11px] shadow-lg">
      <div className="text-[color:var(--text-2)] mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-1.5">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-[color:var(--text-2)]">{p.name}:</span>
          <span className="font-mono text-[color:var(--text-1)]">{formatter ? formatter(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  );
}

const BRAND = {
  primary: "#1BA89F",
  accent: "#FF8A3D",
  info: "#4DB7FF",
  success: "#2FE7A1",
  danger: "#FF5C6C",
  secondary: "#9F7AEA",
};

const PLATFORM_COLORS = {
  Facebook: "#1877F2",
  Instagram: "#E4405F",
  X: "#E2E8F0",
  TikTok: "#A78BFA",
  LinkedIn: "#0A66C2",
};

export default function AdminOverviewPage() {
  const [data, setData] = useState(null);
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  // Default to "real" so admins see real-customer metrics out of the box. The
  // backend treats unknown values as "real" too.
  const [userScope, setUserScope] = useState("real");

  const load = async (windowDays, scopeOverride) => {
    setLoading(true);
    const scope = scopeOverride || userScope;
    try {
      const [d, s] = await Promise.all([
        api.get(`/admin/dashboard/overview?user_scope=${scope}`),
        api.get(`/admin/dashboard/timeseries?days=${windowDays || days}&user_scope=${scope}`),
      ]);
      setData(d.data);
      setSeries(s.data);
    } catch (e) {
      // Surface the actual server detail so admins can diagnose production
      // failures without digging through logs.
      const status = e?.response?.status ?? "network";
      const detail = e?.response?.data?.detail || e?.message || "Unknown error";
      const msg = `Could not load admin metrics (${status}): ${detail}`;
      // eslint-disable-next-line no-console
      console.error("[postmoro] admin/dashboard/overview failed", { status, detail, error: e });
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(days, userScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days, userScope]);

  const planRows = useMemo(() => data?.users?.by_plan || [], [data]);
  const revenueRows = useMemo(() => data?.revenue?.by_plan || [], [data]);
  const platformRows = useMemo(() => data?.usage?.by_platform || [], [data]);
  const visitsRows = useMemo(() => data?.visits?.most_visited || [], [data]);
  const trafficRows = useMemo(() => data?.visits?.traffic_sources || [], [data]);
  const xApi = data?.usage?.x_api;

  if (loading && !data) {
    return (
      <div className="py-20 flex items-center justify-center text-[color:var(--text-3)]" data-testid="admin-loading">
        <Loader2 className="h-5 w-5 mr-2 animate-spin" /> Loading admin dashboard…
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="relative">
      <GradientMesh />
      <div className="relative">
        <PageHeader
          title="Owner Dashboard"
          subtitle="Real-time pulse of Postmoro — users, revenue, posts, and traffic."
          testidPrefix="admin-overview"
          action={
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-0.5" data-testid="admin-window-picker">
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setDays(d)}
                    className={
                      "text-xs px-2.5 py-1 rounded " +
                      (days === d
                        ? "bg-[color:var(--bg-0)] text-[color:var(--text-1)] shadow-sm"
                        : "text-[color:var(--text-3)] hover:text-[color:var(--text-1)]")
                    }
                    data-testid={`admin-window-${d}`}
                  >
                    {d}d
                  </button>
                ))}
              </div>
              <Button
                variant="outline"
                onClick={() => load(days)}
                disabled={loading}
                className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
                data-testid="admin-refresh"
              >
                {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                Refresh
              </Button>
            </div>
          }
        />

        {/* ===== Rebrand Launch Checklist (temporary — remove after cutover) ===== */}
        <div className="mb-5">
          <RebrandChecklist />
        </div>

        {/* ===== User-scope filter + honest classification banner (Phase 13) ===== */}
        {data.classification ? (
          <UserScopeBanner
            classification={data.classification}
            scope={userScope}
            onScopeChange={setUserScope}
            loading={loading}
          />
        ) : null}

        {/* ===== USER METRICS ===== */}
        <SectionHeader title="User metrics" icon={Users} badge={<DataSourceLabel source={data.users?.data_source || "real"} />} />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          <MetricCard icon={Users} label="Total users" value={fmtInt(data.users.total)} tone="brand" testid="metric-users-total" />
          <MetricCard icon={UserPlus} label="New this month" value={fmtInt(data.users.new_this_month)} tone="success" testid="metric-users-new" />
          <MetricCard icon={Activity} label="Active (7d)" value={fmtInt(data.users.active_7d)} tone="info" testid="metric-users-active" />
          <MetricCard icon={Users} label="Free" value={fmtInt(data.users.free)} testid="metric-users-free" />
          <MetricCard icon={CreditCard} label="Paid" value={fmtInt(data.users.paid)} tone="warm" testid="metric-users-paid" />
          <MetricCard icon={XCircle} label="Canceled" value={fmtInt(data.users.canceled)} tone="danger" testid="metric-users-canceled" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
          <GlassCard className="p-4 lg:col-span-2" data-testid="chart-user-growth">
            <div className="flex items-center justify-between mb-2">
              <div className="font-display text-base font-semibold">User growth</div>
              <div className="text-[11px] text-[color:var(--text-3)]">Last {days} days</div>
            </div>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series?.points || []} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="userFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={BRAND.primary} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={BRAND.primary} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(127,138,163,0.10)" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} stroke="#7F8AA3" tick={{ fontSize: 10 }} />
                  <YAxis allowDecimals={false} stroke="#7F8AA3" tick={{ fontSize: 10 }} width={28} />
                  <Tooltip content={<ChartTooltip />} cursor={{ stroke: "rgba(127,138,163,0.2)" }} />
                  <Area type="monotone" dataKey="users" name="New users" stroke={BRAND.primary} fill="url(#userFill)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </GlassCard>
          <GlassCard className="p-4" data-testid="chart-users-by-plan">
            <div className="font-display text-base font-semibold mb-3">Users by plan</div>
            <div className="space-y-2">
              {planRows.map((row) => (
                <div key={row.plan_id} className="flex items-center gap-2" data-testid={`plan-row-${row.plan_id}`}>
                  <div className="text-xs text-[color:var(--text-2)] w-24 truncate">{row.plan_name}</div>
                  <div className="flex-1 h-2 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
                    <div
                      className="h-full bg-[color:var(--brand-primary)]"
                      style={{ width: `${Math.min(100, (row.count / Math.max(1, data.users.total)) * 100)}%` }}
                    />
                  </div>
                  <div className="text-xs font-mono text-[color:var(--text-1)] w-10 text-right">{fmtInt(row.count)}</div>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>

        {/* ===== REVENUE METRICS ===== */}
        <SectionHeader
          title="Revenue metrics"
          icon={DollarSign}
          badge={<DataSourceLabel source={data.revenue?.data_source || "not_connected"} />}
        />
        {data.revenue?.data_source === "not_connected" ? (
          <NotConnectedPanel
            testid="revenue-not-connected"
            message="Stripe billing isn't connected yet."
            helper="Add STRIPE_SECRET_KEY and Price IDs in the backend env to start tracking MRR, churn, and active subscriptions in real time."
          />
        ) : data.revenue?.stripe_backed_subscriptions === 0 ? (
          <NotConnectedPanel
            testid="revenue-no-data"
            message="No Stripe subscriptions yet."
            helper="Once your first customer completes Stripe Checkout, MRR, ARPU, and plan revenue numbers will appear here automatically."
          />
        ) : null}
        {data.revenue?.stripe_backed_subscriptions > 0 ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
              <MetricCard icon={DollarSign} label="MRR (current)" value={fmtMoney(data.revenue.mrr)} tone="warm" testid="metric-mrr" />
              <MetricCard icon={TrendingUp} label="Total revenue" value={fmtMoney(data.revenue.total_revenue)} testid="metric-revenue-total" />
              <MetricCard icon={Repeat} label="Active subs" value={fmtInt(data.revenue.active_subscriptions)} tone="success" testid="metric-active-subs" />
              <MetricCard icon={AlertTriangle} label="Failed payments" value={fmtInt(data.revenue.failed_payments)} tone="danger" testid="metric-failed-payments" />
              <MetricCard icon={XCircle} label="Churn" value={fmtInt(data.revenue.churn_cancellations)} testid="metric-churn" />
              <MetricCard icon={Users} label="ARPU" value={fmtMoney(data.revenue.arpu)} tone="info" testid="metric-arpu" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
          <GlassCard className="p-4 lg:col-span-2" data-testid="chart-revenue">
            <div className="flex items-center justify-between mb-2">
              <div className="font-display text-base font-semibold">Revenue trajectory</div>
              <div className="text-[11px] text-[color:var(--text-3)]">Daily MRR distribution</div>
            </div>
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series?.points || []} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                  <defs>
                    <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={BRAND.accent} stopOpacity={0.45} />
                      <stop offset="100%" stopColor={BRAND.accent} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="rgba(127,138,163,0.10)" vertical={false} />
                  <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} stroke="#7F8AA3" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#7F8AA3" tick={{ fontSize: 10 }} width={36} tickFormatter={(v) => `$${Math.round(v)}`} />
                  <Tooltip content={<ChartTooltip formatter={fmtMoney} />} cursor={{ stroke: "rgba(127,138,163,0.2)" }} />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke={BRAND.accent} fill="url(#revFill)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </GlassCard>
          <GlassCard className="p-4" data-testid="chart-revenue-by-plan">
            <div className="font-display text-base font-semibold mb-3">Revenue by plan</div>
            <div className="space-y-2">
              {revenueRows.map((row) => (
                <div key={row.plan_id} className="flex items-center gap-2" data-testid={`rev-row-${row.plan_id}`}>
                  <div className="text-xs text-[color:var(--text-2)] w-24 truncate">{row.plan_name}</div>
                  <div className="flex-1 h-2 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
                    <div
                      className="h-full bg-[color:var(--accent-warm)]"
                      style={{ width: `${Math.min(100, (row.amount / Math.max(1, data.revenue.mrr)) * 100)}%` }}
                    />
                  </div>
                  <div className="text-xs font-mono text-[color:var(--text-1)] w-16 text-right">{fmtMoney(row.amount)}</div>
                </div>
              ))}
            </div>
          </GlassCard>
            </div>
          </>
        ) : null}

        {/* ===== PLATFORM USAGE ===== */}
        <SectionHeader title="Platform usage" icon={Sparkles} badge={<DataSourceLabel source={data.usage?.data_source || "real"} />} />
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
          <MetricCard icon={Sparkles} label="AI posts generated" value={fmtInt(data.usage.ai_generated)} tone="brand" testid="metric-ai-generated" />
          <MetricCard icon={CalendarIcon} label="Scheduled" value={fmtInt(data.usage.posts_scheduled)} tone="info" testid="metric-scheduled" />
          <MetricCard icon={Send} label="Published" value={fmtInt(data.usage.posts_published)} tone="success" testid="metric-published" />
          <MetricCard icon={XCircle} label="Failed" value={fmtInt(data.usage.posts_failed)} tone="danger" testid="metric-failed" />
          <MetricCard icon={Activity} label="Posts total" value={fmtInt(data.usage.posts_total)} testid="metric-posts-total" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mt-3">
          <GlassCard className="p-4 lg:col-span-2" data-testid="chart-posts-by-platform">
            <div className="font-display text-base font-semibold mb-3">Posts by platform</div>
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={platformRows} margin={{ left: 0, right: 8, top: 8, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(127,138,163,0.10)" vertical={false} />
                  <XAxis dataKey="platform" stroke="#7F8AA3" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} stroke="#7F8AA3" tick={{ fontSize: 10 }} width={28} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(127,138,163,0.08)" }} />
                  <Bar dataKey="count" name="Posts" radius={[6, 6, 0, 0]}>
                    {platformRows.map((row) => (
                      <Cell key={row.platform} fill={PLATFORM_COLORS[row.platform] || BRAND.primary} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </GlassCard>

          <GlassCard
            className={
              "p-4 " +
              (xApi?.level === "danger"
                ? "ring-1 ring-[color:rgba(255,92,108,0.5)]"
                : xApi?.level === "warning"
                ? "ring-1 ring-[color:rgba(255,138,61,0.5)]"
                : "")
            }
            data-testid="x-api-warning-card"
          >
            <div className="font-display text-base font-semibold mb-1 flex items-center gap-2">
              X / Twitter API
              {xApi?.level === "danger" ? (
                <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[color:rgba(255,92,108,0.18)] text-[color:var(--danger)]">
                  Cap risk
                </span>
              ) : xApi?.level === "warning" ? (
                <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[color:rgba(255,138,61,0.18)] text-[color:var(--accent-warm)]">
                  Watch
                </span>
              ) : (
                <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[color:rgba(47,231,161,0.18)] text-[color:var(--success)]">
                  Healthy
                </span>
              )}
            </div>
            <div className="text-xs text-[color:var(--text-2)] leading-relaxed mb-3">
              Tracks tweets published this calendar month against the X v2 free-tier monthly cap.
            </div>
            <div className="flex items-end justify-between">
              <div>
                <div className="font-display text-3xl font-semibold text-[color:var(--text-1)]">
                  {fmtInt(xApi?.published_this_month)}
                </div>
                <div className="text-[11px] text-[color:var(--text-3)]">of {fmtInt(xApi?.free_tier_cap)} tweets / mo</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-[color:var(--text-2)]">
                  {Math.round((xApi?.ratio || 0) * 100)}% used
                </div>
              </div>
            </div>
            <div className="mt-2 h-2 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
              <div
                className={
                  "h-full " +
                  (xApi?.level === "danger"
                    ? "bg-[color:var(--danger)]"
                    : xApi?.level === "warning"
                    ? "bg-[color:var(--accent-warm)]"
                    : "bg-[color:var(--success)]")
                }
                style={{ width: `${Math.min(100, (xApi?.ratio || 0) * 100)}%` }}
              />
            </div>
          </GlassCard>
        </div>

        {/* ===== LIVE PLATFORM CONNECTIONS ===== */}
        <SectionHeader
          title="Platform connections"
          icon={Link2}
          badge={<DataSourceLabel source={data.connections?.data_source || "real"} />}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-connections-grid">
          {(data.connections?.items || []).map((c) => {
            const isLive = c.status === "live";
            return (
              <GlassCard key={c.platform} className="p-4" data-testid={`admin-connection-${c.platform.toLowerCase()}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-display text-base font-semibold text-[color:var(--text-1)]">{c.platform}</div>
                    {isLive ? (
                      <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[color:var(--success)]">
                        <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--success)]" /> Live
                      </div>
                    ) : (
                      <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[color:var(--warm)]">
                        <AlertCircle className="h-3 w-3" /> Requires approval
                      </div>
                    )}
                  </div>
                  <Link2 className={`h-4 w-4 ${isLive ? "text-[color:var(--success)]" : "text-[color:var(--text-3)]"}`} />
                </div>
                <div className="mt-3">
                  {isLive ? (
                    <>
                      <div className="font-display text-2xl font-semibold text-[color:var(--text-1)]" data-testid={`admin-connection-${c.platform.toLowerCase()}-count`}>
                        {fmtInt(c.connected_count)}
                      </div>
                      <div className="text-[11px] text-[color:var(--text-3)] mt-0.5">connected accounts</div>
                    </>
                  ) : (
                    <>
                      <div className="text-sm text-[color:var(--text-2)] leading-snug">{c.note || "Publishing gated pending platform approval."}</div>
                      <div className="mt-2 text-[11px] text-[color:var(--text-3)]">
                        {c.connected_count > 0 ? `${fmtInt(c.connected_count)} real connections waiting` : "No connections yet"}
                        {c.demo_count > 0 ? ` · ${fmtInt(c.demo_count)} admin test` : ""}
                      </div>
                    </>
                  )}
                </div>
              </GlassCard>
            );
          })}
        </div>

        {/* ===== WEBSITE / APP VISITS ===== */}
        <SectionHeader title="Website & app visits" icon={Eye} badge={<DataSourceLabel source={data.visits?.data_source || "not_connected"} />} />
        {data.visits?.data_source === "not_connected" ? (
          <NotConnectedPanel
            testid="visits-not-connected"
            message="Analytics not connected yet."
            helper="Once you wire a /api/track ping from the landing page (or hook in Plausible / GA), traffic, conversion rate, top pages, and traffic sources will appear here."
          />
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard icon={Eye} label="Total visits" value={fmtInt(data.visits.total)} tone="info" testid="metric-visits-total" />
          <MetricCard icon={Eye} label="Landing page visits" value={fmtInt(data.visits.landing)} testid="metric-visits-landing" />
          <MetricCard icon={UserPlus} label="Signups from visits" value={fmtInt(data.visits.signups_from_visits)} tone="success" testid="metric-signups-from-visits" />
          <MetricCard
            icon={TrendingUp}
            label="Conversion rate"
            value={`${((data.visits.conversion_rate || 0) * 100).toFixed(2)}%`}
            tone="warm"
            testid="metric-conversion-rate"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mt-3">
          <GlassCard className="p-4" data-testid="card-most-visited">
            <div className="font-display text-base font-semibold mb-3">Most visited pages</div>
            <div className="space-y-2">
              {visitsRows.map((row) => (
                <div key={row.path} className="flex items-center gap-2">
                  <code className="text-[11px] font-mono rounded bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] px-1.5 py-0.5 text-[color:var(--text-1)] truncate max-w-[160px]">
                    {row.path}
                  </code>
                  <div className="flex-1 h-2 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
                    <div
                      className="h-full bg-[color:var(--info)]"
                      style={{ width: `${Math.min(100, (row.count / Math.max(1, visitsRows[0]?.count || 1)) * 100)}%` }}
                    />
                  </div>
                  <div className="text-xs font-mono text-[color:var(--text-1)] w-12 text-right">{fmtInt(row.count)}</div>
                </div>
              ))}
            </div>
          </GlassCard>
          <GlassCard className="p-4" data-testid="card-traffic-sources">
            <div className="font-display text-base font-semibold mb-3">Traffic sources</div>
            <div className="space-y-2">
              {trafficRows.map((row) => (
                <div key={row.source} className="flex items-center gap-2">
                  <div className="text-xs text-[color:var(--text-2)] w-28 truncate">{row.source}</div>
                  <div className="flex-1 h-2 rounded-full bg-[color:var(--surface-2)] overflow-hidden">
                    <div
                      className="h-full bg-[color:var(--brand-primary)]"
                      style={{ width: `${Math.min(100, (row.count / Math.max(1, trafficRows[0]?.count || 1)) * 100)}%` }}
                    />
                  </div>
                  <div className="text-xs font-mono text-[color:var(--text-1)] w-12 text-right">{fmtInt(row.count)}</div>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
          </>
        )}
      </div>
    </div>
  );
}

function SectionHeader({ title, icon: Icon, badge }) {
  return (
    <div className="flex items-center gap-2 mt-8 mb-3">
      {Icon ? (
        <div className="h-7 w-7 rounded-md bg-[color:var(--surface-2)] border border-[color:var(--border-subtle)] flex items-center justify-center text-[color:var(--accent-warm)]">
          <Icon className="h-3.5 w-3.5" />
        </div>
      ) : null}
      <div className="font-display text-lg font-semibold">{title}</div>
      {badge}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Phase 13 — User-scope filter and honest classification banner.
// Lets admins toggle between Real / Test / All users. Backend filters every
// user-derived metric (totals, plans, growth chart, posts, MRR) accordingly,
// so the cards above match whatever scope is active.
// -----------------------------------------------------------------------------
function UserScopeBanner({ classification, scope, onScopeChange, loading }) {
  const summary = classification || {};
  const totalInDb = Number(summary.total_in_db || 0);
  const real = Number(summary.real || 0);
  const test = Number(summary.test || 0);
  const admin = Number(summary.admin || 0);
  const hasTestUsers = test > 0;
  const options = [
    { value: "real", label: "Real users only", count: real + admin },
    { value: "test", label: "Test users only", count: test },
    { value: "all", label: "Include test users", count: totalInDb },
  ];
  return (
    <div
      className="mb-5 rounded-lg border border-[color:var(--border-subtle)] bg-[color:var(--surface-1)] p-3.5"
      data-testid="admin-user-scope-banner"
    >
      <div className="flex items-start gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-3)]">User scope</div>
            {loading ? <Loader2 className="h-3 w-3 animate-spin text-[color:var(--text-3)]" /> : null}
          </div>
          <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="User scope filter" data-testid="admin-user-scope-tabs">
            {options.map((opt) => {
              const active = scope === opt.value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => onScopeChange(opt.value)}
                  data-testid={`admin-user-scope-${opt.value}`}
                  className={
                    "text-xs px-3 py-1.5 rounded-md border transition inline-flex items-center gap-1.5 " +
                    (active
                      ? "border-[color:var(--brand-primary)] bg-[color:rgba(27,168,159,0.10)] text-[color:var(--text-1)]"
                      : "border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-2)] hover:text-[color:var(--text-1)]")
                  }
                >
                  <span>{opt.label}</span>
                  <span className="font-mono text-[10px] text-[color:var(--text-3)]">({opt.count.toLocaleString()})</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1 text-[11px] text-[color:var(--text-3)] min-w-[180px]">
          <div className="font-mono">
            <span className="text-[color:var(--success)]">{real.toLocaleString()}</span> real ·{" "}
            <span className="text-[color:var(--warm)]">{test.toLocaleString()}</span> test ·{" "}
            <span className="text-[color:var(--info)]">{admin.toLocaleString()}</span> admin
          </div>
          <div className="text-[10px]">of {totalInDb.toLocaleString()} total accounts</div>
          <Link
            to="/admin/customers?scope=test"
            className="text-[11px] text-[color:var(--brand-primary)] hover:underline"
            data-testid="admin-user-scope-cleanup-link"
          >
            Review {hasTestUsers ? `${test.toLocaleString()} ` : ""}suspected test accounts →
          </Link>
        </div>
      </div>

      {hasTestUsers && scope !== "test" ? (
        <div
          className="mt-3 pt-3 border-t border-[color:var(--border-subtle)] flex items-start gap-2 text-[11px] text-[color:var(--text-2)]"
          data-testid="admin-user-scope-notice"
        >
          <AlertTriangle className="h-3.5 w-3.5 text-[color:var(--warm)] mt-0.5 shrink-0" />
          <span>
            Production database contains <span className="font-medium text-[color:var(--text-1)]">{test.toLocaleString()} test accounts</span>. Metrics shown
            {scope === "real" ? " exclude them by default." : " include them because scope is \"all\"."}{" "}
            Use the toggle to switch.
          </span>
        </div>
      ) : null}
    </div>
  );
}
